import React, { useState } from 'react';
import { Box, Typography, Button, Stack, Chip, Divider, IconButton } from '@mui/material';
import DirectionsIcon from '@mui/icons-material/Directions';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalCarWashIcon from '@mui/icons-material/LocalCarWash';
import StarIcon from '@mui/icons-material/Star';
import PlaceIcon from '@mui/icons-material/Place';
import CloseIcon from '@mui/icons-material/Close';
import { Carwash } from '../../types/CarwashType';
import { formatDistance, buildDirectionsUrl, formatAddress } from '../../utils/carwashHelpers';

const APP_STORE_URL = 'https://apps.apple.com/br/app/washpass/id6503068980';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=washpass.app&hl=pt_BR';

interface Props {
  carwash: Carwash;
  onClose: () => void;
}

function PartnerInfoWindow({ carwash, onClose }: Props) {
  function handleOpenApp() {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
    const isAndroid = /android/i.test(userAgent);

    if (!isIOS && !isAndroid) {
      window.location.href = '/app-download';
      return;
    }

    const washId = carwash?.id;
    const deepLinkPath = washId != null ? `car-wash-profile?washId=${washId}` : '';

    const appUrl = isIOS
      ? `washpass://${deepLinkPath}`
      : `intent://${deepLinkPath}#Intent;scheme=washpass;package=washpass.app;end`;
    const fallbackUrl = isIOS ? APP_STORE_URL : PLAY_STORE_URL;

    let appOpened = false;
    const timeoutRef: { current: ReturnType<typeof setTimeout> | null } = { current: null };

    const handleBlur = () => {
      appOpened = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      window.removeEventListener('blur', handleBlur);
    };

    window.addEventListener('blur', handleBlur);
    window.location.href = appUrl;

    timeoutRef.current = setTimeout(() => {
      if (!appOpened) {
        window.removeEventListener('blur', handleBlur);
        window.location.href = fallbackUrl;
      }
    }, 2000);
  }
  const [imgError, setImgError] = useState(false);
  const showFallback = !carwash.logo || imgError;

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 5,
        bgcolor: 'white',
        borderRadius: 2,
        boxShadow: '0 4px 24px rgba(0,0,0,0.22)',
        p: 2,
        minWidth: 260,
        maxWidth: 300,
        width: 'calc(100% - 32px)',
      }}
    >
      <IconButton
        size="small"
        onClick={onClose}
        sx={{ position: 'absolute', top: 6, right: 6 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {showFallback ? (
        <Box
          sx={{
            width: '100%',
            height: 72,
            bgcolor: '#e0f7ff',
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
          }}
        >
          <LocalCarWashIcon sx={{ fontSize: 36, color: '#00b4e4', opacity: 0.75 }} />
        </Box>
      ) : (
        <Box
          component="img"
          src={carwash.logo!}
          alt={carwash.car_wash_name}
          onError={() => setImgError(true)}
          sx={{ width: '100%', height: 72, objectFit: 'contain', mb: 1 }}
        />
      )}

      <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3, mb: 0.5, pr: 3 }}>
        {carwash.car_wash_name}
      </Typography>

      {carwash.average_rating !== undefined && carwash.average_rating > 0 && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
          <StarIcon sx={{ fontSize: 14, color: '#FFB400' }} />
          <Typography variant="caption">{carwash.average_rating.toFixed(1)}</Typography>
        </Stack>
      )}

      <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mb: 0.5 }}>
        <PlaceIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.1 }} />
        <Typography variant="caption" color="text.secondary">
          {formatAddress(carwash)}
        </Typography>
      </Stack>

      {carwash.distance !== undefined && (
        <Chip
          label={formatDistance(carwash.distance)}
          size="small"
          sx={{ mb: 1, bgcolor: '#e3f6fd', color: '#00b4e4', fontWeight: 600, fontSize: 11 }}
        />
      )}

      <Divider sx={{ my: 0.75 }} />

      <Stack spacing={1}>
        <Button
          variant="contained"
          size="small"
          fullWidth
          startIcon={<DirectionsIcon />}
          href={buildDirectionsUrl(carwash)}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textTransform: 'none', bgcolor: '#00b4e4', '&:hover': { bgcolor: '#0099c6' } }}
        >
          Como chegar
        </Button>

        <Button
          variant="outlined"
          size="small"
          fullWidth
          startIcon={<InfoOutlinedIcon />}
          onClick={handleOpenApp}
          sx={{ textTransform: 'none', borderColor: '#00b4e4', color: '#00b4e4', '&:hover': { borderColor: '#0099c6', color: '#0099c6', bgcolor: '#f0fbff' } }}
        >
          Ver mais informações
        </Button>
      </Stack>
    </Box>
  );
}

export default PartnerInfoWindow;
